import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import NavBar from '../components/NavBar';
import OnBoardingProgressBar from '../components/onboarding/OnBoardingProgressBar';
import OnBoardingSteps from '../components/onboarding/OnBoardingSteps';
import PricingPlan from '../components/onboarding/PricingPlan';
import TellAboutYourBusiness from '../components/onboarding/TellAboutYourBusiness';
import BusinessLocationForm from '../components/onboarding/BusinessLocationForm';
import ShopPhotoUpload from '../components/onboarding/ShopPhotoUpload';
import WhatDoYouOffer from '../components/onboarding/WhatDoYouOffer';
import WorkingHours from '../components/onboarding/WorkingHours';
import ReviewDetailsStep from '../components/onboarding/ReviewDetailsStep';
import CreateAccountStep from '../components/onboarding/CreateAccountStep';
import { onboardingSteps } from '../utils/dummydata';
import { storage } from '../firebase/firebaseConfig';
import { ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
import { buildSubscriptionPaymentRequest } from "../utils/paystackSubscription";
import { functionsBaseUrl } from "../config/runtime";
import { authenticatedJson, ensureGuestSession } from "../utils/guestSession";

const OnBoarding = () => {
    const navigate = useNavigate();
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [creatingAccount, setCreatingAccount] = useState(false);
    const [error, setError] = useState("");
    const [form, setForm] = useState({
        shopName: "",
        ownerName: "",
        shopEmail: "",
        phone: "",
        reviews: "",
        description: "",
        streetAddress: "",
        city: "",
        province: "",
        postalCode: "",
        shopPhotoPreview: "",
        shopPhotoFile: null,
        shopPhotoFileName: "",
        selectedSupplies: [],
        inkTypes: [],
        customSupplies: [],
        workingHours: {
            Monday: { open: true, from: "09:00", to: "18:00" },
            Tuesday: { open: true, from: "09:00", to: "18:00" },
            Wednesday: { open: true, from: "09:00", to: "18:00" },
            Thursday: { open: true, from: "09:00", to: "18:00" },
            Friday: { open: true, from: "09:00", to: "18:00" },
            Saturday: { open: true, from: "09:00", to: "18:00" },
            Sunday: { open: false, from: "09:00", to: "18:00" },
        },
        password: "",
        confirmPassword: "",

    });
    const [step, setStep] = useState(1);
    const currentStepData = onboardingSteps[step - 1];

    const stepTitle =
        step === 5
            ? selectedPlan.key === "supplier"
                ? "What supplies do you offer?"
                : "What services do you offer?"
            : currentStepData.title;

    const stepDescription =
        step === 5
            ? selectedPlan.key === "supplier"
                ? "Select the supply categories you stock - e.g. printing machines, signage supplies, sublimation materials. Customers will search and filter by these."
                : "Select all that apply. Customers will search and filter by these tags."
            : currentStepData.description;

    const [emailExists, setEmailExists] = useState(false);
    const [checkingEmail, setCheckingEmail] = useState(false);


    const isValidEmail = (email) =>
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

    const canContinue = () => {
        switch (step) {
            case 1:
                return !!selectedPlan;

            case 2:
                return (
                    form.shopName?.trim() &&
                    form.ownerName?.trim() &&
                    form.shopEmail?.trim() &&
                    isValidEmail(form.shopEmail)
                );

            case 3:
                return (
                    form.streetAddress?.trim() &&
                    form.city?.trim()
                );

            case 4:
                return true; // optional step

            case 5:
                return (
                    (form.selectedSupplies?.length > 0) ||
                    (form.customSupplies?.length > 0)
                );

            case 6:
                return true; // assume always valid from UI

            case 7:
                return true;

            case 8:
                return (
                    form.shopEmail?.trim() &&
                    isValidEmail(form.shopEmail) &&
                    form.password?.length >= 6 &&
                    form.password === form.confirmPassword &&
                    !emailExists
                );

            default:
                return false;
        }
    };



    const handleCompleteSetup = async () => {
        try {
            setCreatingAccount(true);

            const selected = selectedPlan;
            // Establish the anonymous Firebase identity before any upload or
            // payment request. The Functions endpoint verifies this token.
            await ensureGuestSession();

            let shopPhotoURL = "";
            let tempFileName = "";

            if (form.shopPhotoFile) {
                const file = form.shopPhotoFile;
                const guest = await ensureGuestSession();

                const extension = file.name.split(".").pop();

                tempFileName =
                    `onboarding/${guest.uid}/${Date.now()}-${Math.random()
                        .toString(36)
                        .substring(2)}.${extension}`;

                const imageRef = storageRef(storage, tempFileName);

                await uploadBytes(imageRef, file);

                shopPhotoURL = await getDownloadURL(imageRef);
            }

            const pendingData = {
                selectedPlan,
                form: {
                    ...form,
                    shopPhotoFile: null,
                    shopPhotoPreview: "",
                    shopPhotoURL,
                    shopPhotoFileName: tempFileName,
                },
            };

            localStorage.setItem(
                "pendingOnboarding",
                JSON.stringify(pendingData)
            );

            const response = await authenticatedJson(
                `${functionsBaseUrl}/initializeSubscriptionPaystackTransaction`,
                buildSubscriptionPaymentRequest({
                    email: form.shopEmail,
                    planKey: selected.key,
                    callbackUrl: `${window.location.origin}/verify-payment?plan=${selected.key}`,
                })
            );

            const authorizationUrl = response.data.authorization_url;

            window.location.href = authorizationUrl;

        } catch (error) {
            console.error(error);
            setError(error.message);
            setCreatingAccount(false);
        }
    };



    const handleEmailBlur = async () => {
        const emailValid = form.shopEmail.length > 0 && isValidEmail(form.shopEmail);
        if (!emailValid) return;

        // Account existence is deliberately not exposed before payment.
        setEmailExists(false);
        setCheckingEmail(false);
    };

    return (
        <div className="min-h-full bg-[#f8f8fa] flex flex-col font-['Plus_Jakarta_Sans',sans-serif]">
            <Helmet>
                <title>Create account - Signet Print</title>
                <meta
                    name="description"
                    content="Create a new account on Signet Print."
                />
            </Helmet>
            <NavBar onBoarding={true} step={step} />
            <OnBoardingProgressBar progress={currentStepData.progress} />
            <main className="max-w-[1200px] mx-auto w-full px-[16px] md:px-[40px] py-[24px] md:pt-[36px] pb-[110px] md:pb-[100px] flex-1 flex flex-col ">

                <OnBoardingSteps activeStep={step} />

                <section className="w-full max-w-[640px] mx-auto flex-1">
                    <div className="flex flex-col gap-[24px]">

                        {/* Header */}
                        <div>
                            <p className="font-semibold text-[13px] text-[#ff5323] tracking-[0.5px] uppercase mb-[6px]">
                                Step {currentStepData.step}
                            </p>
                            <h2 className="font-semibold text-[26px] text-[#212326] mb-[8px]">
                                {stepTitle} {currentStepData.optional && <span className="font-semibold text-[16px] text-[#9298a0]">(optional)</span>}
                            </h2>
                            <p className="font-medium text-[15px] text-[#717182] leading-[1.6]">
                                {stepDescription}
                            </p>
                        </div>

                        {step === 1 && (
                            <PricingPlan selectedPlan={selectedPlan} setSelectedPlan={setSelectedPlan} />
                        )}

                        {step === 2 && (
                            <TellAboutYourBusiness form={form} setForm={setForm} />
                        )}

                        {step === 3 && (
                            <BusinessLocationForm form={form} setForm={setForm} />
                        )}

                        {step === 4 && (
                            <ShopPhotoUpload form={form} setForm={setForm} />
                        )}

                        {step === 5 && (
                            <WhatDoYouOffer form={form} setForm={setForm} selectedPlan={selectedPlan} />
                        )}

                        {step === 6 && (
                            <WorkingHours form={form} setForm={setForm} />
                        )}

                        {step === 7 && (
                            <ReviewDetailsStep
                                form={form}
                                selectedPlan={selectedPlan}
                                setStep={setStep}
                            />
                        )}

                        {step === 8 && (
                            <CreateAccountStep
                                form={form}
                                setForm={setForm}
                                error={error}
                                handleEmailBlur={handleEmailBlur}
                                emailExists={emailExists}
                                checkingEmail={checkingEmail}
                            />
                        )}


                    </div>
                </section>
            </main>

            <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-[#e4e5e7] z-20">
                <div className="max-w-[1200px] mx-auto px-[16px] md:px-[40px] py-[20px] flex items-center justify-between">
                    <div className="flex items-center gap-[12px] min-w-0">
                        {step > 1 && (
                            <button
                                onClick={() => setStep(step - 1)}
                                className="flex items-center gap-[6px] px-[18px] py-[11px] rounded-[10px] font-semibold text-[14px] text-[#212326] hover:bg-[#f4f5f6] transition-colors underline underline-offset-2"
                            >
                                Back
                            </button>
                        )}
                    </div>
                    <button
                        disabled={!canContinue() || creatingAccount}
                        onClick={() => {
                            if (step === 8) {
                                handleCompleteSetup();
                            } else {
                                setStep(step + 1);
                            }
                        }}
                        className={`flex items-center gap-[8px] px-[20px] md:px-[28px] py-[12px] rounded-[10px] font-semibold text-[15px] transition-all shrink-0 
                            ${canContinue() ?
                                'bg-[#ff5323] text-white hover:bg-[#e64a1f] shadow-[0px_2px_8px_rgba(255,83,35,0.3)]' :
                                'bg-[#e4e5e7] text-[#9298a0] cursor-not-allowed'}`}
                    >
                        {step === 8
                            ? (creatingAccount ? "Creating account..." : "Complete Setup")
                            : "Continue"
                        }
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-arrow-right w-[16px] h-[16px]">
                            <path d="M5 12h14">
                            </path>
                            <path d="m12 5 7 7-7 7"></path>
                        </svg>
                    </button>
                </div>
            </div>
        </div>
    )
}

export default OnBoarding;
